import { Box, Button } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { useLocalPriceDisplay, useTranslation } from "@outschool/localization";
import {
  FilterDropdown,
  FundingProgramButton,
  useSearchFiltersString,
} from "@outschool/ui-components-website";
import { OverlayBackdrop } from "@outschool/ui-legacy-component-library";
import ldIsEmpty from "lodash/isEmpty";
import ldIsEqual from "lodash/isEqual";
import ldPick from "lodash/pick";
import ldUniq from "lodash/uniq";
import React, { useState } from "react";

import * as Activity from "../../../../shared/Activity";
import usePriceFilterString from "../../../hooks/usePriceFilterString";
import AgeFilters from "../../search/AgeFilters";
import CapacityFilter, {
  useCapacityToString,
} from "../../search/CapacityFilter";
import CreditFilter from "../../search/CreditFilter";
import DateFilters from "../../search/DateFilters";
import PricePerMeetingFilter from "../../search/PricePerMeetingFilter";
import TimeFilters from "../../search/TimeFilters";
import { useMembershipRequirement } from "../../subscriptions/hooks/useMembershipRequirement";
import { createAgeString } from "../util";
import { CategoryMoreFilters } from "./CategoryMoreFilters";
import { FiltersToShowType } from "./FiltersToShow";

export const CategoryDesktopFilters = ({
  resetSearchFilters,
  updateSearchFilters,
  searchFilters,
  filtersToShow,
}: {
  resetSearchFilters?: () => void;
  updateSearchFilters: (payload: SearchFiltersType) => void;
  searchFilters: SearchFiltersType;
  filtersToShow: FiltersToShowType;
}) => {
  const { t } = useTranslation(
    "client\\components\\categories\\CategoryDesktopFilters",
  );
  const [openFilterDropdown, setOpenFilterDropdown] = useState<null | string>(
    null,
  );
  const closeFilterDropdown = () => setOpenFilterDropdown(null);

  const { displayPricePerMeeting } = useLocalPriceDisplay();

  const { getTranslatedPricePerMeetingStrings } = usePriceFilterString();

  const { scheduleFiltersToString, dateFiltersToString } =
    useSearchFiltersString();

  const displayCapacityString = useCapacityToString();

  const { showCredits } = useMembershipRequirement();

  const timeFilters =
    filtersToShow.dow && filtersToShow.time
      ? SearchFilters.TIME_FILTERS
      : filtersToShow.dow && !filtersToShow.time
      ? ["dow"]
      : ["startAfterTime", "endByTime"];

  const timeDayTitle =
    filtersToShow.dow && filtersToShow.time
      ? t("Day or time")
      : filtersToShow.dow && !filtersToShow.time
      ? t("Day")
      : t("Time");

  const scheduleFilters = ldPick(searchFilters, timeFilters);

  const dateFilters = ldPick(searchFilters, [
    ...SearchFilters.DATE_FILTERS,
    "dow",
  ]);

  const pricePerMeetingFilters = ldPick(
    searchFilters,
    SearchFilters.PRICE_PER_MEETING_FILTERS,
  );

  const priceCreditsPerMeetingFilters = ldPick(
    searchFilters,
    SearchFilters.PRICE_CREDITS_PER_MEETING_FILTERS,
  );

  const capacityFilters = ldPick(searchFilters, SearchFilters.CAPACITY_FILTERS);

  const ages: number[] = searchFilters.age
    ? ldUniq(
        searchFilters.age
          .split(",")
          .map(age => +age)
          .filter(age => age <= Activity.ageMax),
      )
    : [];

  const buttonSx = {
    padding: "1.1rem",
    "& > svg": {
      paddingTop: 0,
    },
  };

  return (
    <>
      {openFilterDropdown && <OverlayBackdrop onClick={closeFilterDropdown} />}
      <Box
        flex
        sx={{
          gap: "8px",
          alignItems: "center",
          zIndex: openFilterDropdown ? 999 : 1,
          flexWrap: "wrap",
        }}
      >
        <FundingProgramButton
          searchFilters={searchFilters}
          updateSearchFilters={updateSearchFilters}
        />
        {/* Age  */}
        {filtersToShow.age && (
          <FilterDropdown
            filterName={t`Age`}
            filterValue={
              ages.length
                ? `${t("Age", {
                    count: ages.length,
                    defaultValue_plural: "Ages",
                  })} ${createAgeString(ages)}`
                : undefined
            }
            open={openFilterDropdown === "age"}
            onClose={closeFilterDropdown}
            onOpen={() => setOpenFilterDropdown("age")}
            buttonSx={buttonSx}
          >
            <AgeFilters
              value={ages}
              onChange={value => {
                updateSearchFilters({
                  age: value.length ? value.join(",") : "",
                });
                closeFilterDropdown();
              }}
              onCancel={closeFilterDropdown}
            />
          </FilterDropdown>
        )}
        {/* Time  */}
        {(filtersToShow.dow || filtersToShow.time) && (
          <FilterDropdown
            filterName={timeDayTitle}
            filterValue={scheduleFiltersToString(scheduleFilters)}
            open={openFilterDropdown === "schedule"}
            onClose={closeFilterDropdown}
            onOpen={() => setOpenFilterDropdown("schedule")}
            buttonSx={buttonSx}
          >
            <TimeFilters
              value={scheduleFilters}
              onChange={searchFilters => {
                updateSearchFilters({ ...searchFilters });
                closeFilterDropdown();
              }}
              onCancel={closeFilterDropdown}
              hideDaySelector={!filtersToShow.dow}
              hideTimeOfDay={!filtersToShow.time}
            />
          </FilterDropdown>
        )}
        {/* Date  */}
        {filtersToShow.date && (
          <FilterDropdown
            filterName={t`Date`}
            filterValue={dateFiltersToString(dateFilters)}
            open={openFilterDropdown === "date"}
            onClose={closeFilterDropdown}
            onOpen={() => setOpenFilterDropdown("date")}
            buttonSx={buttonSx}
          >
            <DateFilters
              value={dateFilters}
              onShortcutSelected={searchFilters => {
                updateSearchFilters({ ...searchFilters });
                closeFilterDropdown();
              }}
              onChange={searchFilters => {
                updateSearchFilters({ ...searchFilters });
                closeFilterDropdown();
              }}
              onCancel={closeFilterDropdown}
            />
          </FilterDropdown>
        )}

        {/* Class Size  */}
        {filtersToShow.classSize && (
          <FilterDropdown
            filterName={t`Class size`}
            filterValue={
              ldIsEqual(capacityFilters, {})
                ? undefined
                : // @ts-ignore TS(2345): Argument of type 'Partial<SearchFilters>' is not a... Remove this comment to see the full error message
                  displayCapacityString(capacityFilters)
            }
            open={openFilterDropdown === "capacity"}
            onClose={closeFilterDropdown}
            onOpen={() => setOpenFilterDropdown("capacity")}
            buttonSx={buttonSx}
          >
            <CapacityFilter
              value={capacityFilters}
              onChange={(
                value: string | { capacityMin?: number; capacityMax?: number },
              ) => {
                // `value` is a string if user clicks to "Apply" without making a selection
                if (typeof value === "object") {
                  updateSearchFilters({
                    capacityMin: null,
                    capacityMax: null,
                    ...value,
                  });
                }

                closeFilterDropdown();
              }}
              onCancel={closeFilterDropdown}
            />
          </FilterDropdown>
        )}
        {/* CREDIT FILTER */}
        {filtersToShow.price && showCredits && (
          <CreditFilter
            creditFilterValue={priceCreditsPerMeetingFilters}
            handleChangesExternally
            open={openFilterDropdown === "priceCreditsPerMeeting"}
            onOpen={() => setOpenFilterDropdown("priceCreditsPerMeeting")}
            onClose={closeFilterDropdown}
            buttonSx={buttonSx}
            handleImplicitFilterChange={(_category: string, value: object) => {
              updateSearchFilters({
                ...(ldIsEmpty(value)
                  ? { priceCreditsPerMeetingMax: 0 }
                  : value),
              });
              closeFilterDropdown();
            }}
            isCourse={false}
          />
        )}
        {/* PRICE FILTER */}
        {filtersToShow.price && !showCredits && (
          <FilterDropdown
            filterName={t`Any price`}
            filterValue={
              ldIsEmpty(pricePerMeetingFilters) ||
              ldIsEqual(pricePerMeetingFilters, {
                pricePerMeetingMax: 0,
              })
                ? undefined
                : displayPricePerMeeting(
                    pricePerMeetingFilters as any,
                    getTranslatedPricePerMeetingStrings,
                  )
            }
            open={openFilterDropdown === "pricePerMeeting"}
            onClose={closeFilterDropdown}
            onOpen={() => setOpenFilterDropdown("pricePerMeeting")}
            buttonSx={buttonSx}
          >
            <PricePerMeetingFilter
              value={pricePerMeetingFilters}
              onChange={value => {
                updateSearchFilters({
                  ...(ldIsEmpty(value) ? { pricePerMeetingMax: 0 } : value),
                });
                closeFilterDropdown();
              }}
              onCancel={closeFilterDropdown}
            />
          </FilterDropdown>
        )}
        {/* More */}
        <CategoryMoreFilters
          searchFilters={searchFilters}
          updateListings={updateSearchFilters}
          closeFilterDropdown={closeFilterDropdown}
          openFilterDropdown={openFilterDropdown}
          setOpenFilterDropdown={setOpenFilterDropdown}
          filtersToShow={filtersToShow}
        />
        <Button
          size="small"
          variant="link"
          onClick={(e: $TSFixMe) => {
            // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
            resetSearchFilters();
            closeFilterDropdown();
            (e.currentTarget as HTMLElement).blur();
          }}
        >
          {t("Reset Filters")}
        </Button>
      </Box>
    </>
  );
};
